import {
  KebabHorizontalIcon,
  PencilIcon,
  PlusIcon,
  StackIcon,
  SyncIcon,
  TrashIcon,
} from "@primer/octicons-react";
import {
  ActionList,
  ActionMenu,
  Box,
  useConfirm,
} from "@primer/react";
import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../components/ui/button";
import DataGrid from "../../../components/data-grid/DataGrid";
import { IDataGridColumn } from "../../../components/data-grid/type/IDataGridProps";
import Text from "../../../components/ui/text";
import { useCommonContext } from "../../../contexts/common";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import { ePageBaseStatus } from "../../../models/ePageBaseStatus";
import { ITrangThaiDangKyAn } from "../../../models/response/category/ITrangThaiDangKyAn";
import { actions } from "../../../state/actions/actionsWrapper";
import { RootState } from "../../../state/reducers";
import TrangThaiDangKyAnEditModal from "./TrangThaiDangKyAnEditModal";

const TrangThaiDangKyAnPage = () => {
  const { status, trangThaiDangKyAns, isShowEditModal } = useSelector(
    (x: RootState) => x.danhMucWrapper.trangThaiDangKyAn
  );
  const { dm_coso_id } = useCommonSelectedHook();
  const { translate } = useCommonContext();
  const confirm = useConfirm();
  const dispatch = useDispatch();

  // Phân quyền các chức năng
  const canView = true;
  const canAdd = true;
  const canEdit = true;
  const canDelete = true;

  const handleReload = useCallback(() => {
    if (!canView) return;
    dispatch(actions.danhMucWrapper.trangThaiDangKyAn.LOAD_START(undefined));
  }, [canView, dispatch]);

  const handleDelete = useCallback(async (id: number) => {
    if (!canDelete) return;
    if (
      await confirm({
        content: translate("Base.Label.DeleteConfirm.Content"),
        title: translate("Base.Label.Confirm"),
        cancelButtonContent: translate("Base.Label.Close"),
        confirmButtonContent: translate("Base.Label.Delete"),
        confirmButtonType: "danger",
      })
    ) {
      dispatch(actions.danhMucWrapper.trangThaiDangKyAn.DELETE_START(id));
    }
  }, [canDelete, confirm, dispatch, translate]);

  useEffect(() => {
    if (
      canView &&
      (status === ePageBaseStatus.is_not_initialization ||
        status === ePageBaseStatus.is_need_reload)
    ) {
      handleReload();
    }
  }, [status, canView, handleReload]);

  const filteredData = useMemo(() => {
    if (trangThaiDangKyAns.length > 0 && dm_coso_id) {
      return trangThaiDangKyAns.filter(x => x.dm_coso_id === dm_coso_id);
    }
    return trangThaiDangKyAns;
  }, [dm_coso_id, trangThaiDangKyAns]);

  const columns: IDataGridColumn[] = useMemo(() => [
    {
      dataField: "trang_thai",
      caption: translate("Trạng thái"),
      cellRender: (data: ITrangThaiDangKyAn) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              display: "inline-flex",
              verticalAlign: "sub",
              lineHeight: 1,
              mr: 1,
            }}
          >
            <div style={{ color: "#62825D" }}>
              <StackIcon size={14} />{" "}
            </div>
          </Box>
          <span style={{ fontWeight: "500" }}>{data.trang_thai}</span>
        </Box>
      ),
    },
    {
      dataField: "trang_thai_en",
      caption: translate("Trạng thái (EN)"),
    },
    ...(canEdit || canDelete ? [{
      id: "cmd",
      caption: "",
      width: 50,
      align: "center" as const,
      cellRender: (data: ITrangThaiDangKyAn) => (
        <ActionMenu>
          <ActionMenu.Button
            icon={KebabHorizontalIcon}
            variant="invisible"
            sx={{ m: -1 }}
          >
            &nbsp;
          </ActionMenu.Button>
          <ActionMenu.Overlay width="auto">
            <ActionList>
              {canEdit && (
                <ActionList.Item
                  onSelect={() => {
                    dispatch(
                      actions.danhMucWrapper.trangThaiDangKyAn.SHOW_EDIT_MODAL(data)
                    );
                  }}
                >
                  <ActionList.LeadingVisual>
                    <PencilIcon />
                  </ActionList.LeadingVisual>
                  <Text text="Base.Label.Edit" />
                </ActionList.Item>
              )}
              {canDelete && (
                <ActionList.Item
                  variant="danger"
                  onSelect={() => {
                    handleDelete(data.id);
                  }}
                >
                  <ActionList.LeadingVisual>
                    <TrashIcon />
                  </ActionList.LeadingVisual>
                  <Text text="Base.Label.Delete" />
                </ActionList.Item>
              )}
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      ),
    }] : []),
  ], [canDelete, canEdit, dispatch, handleDelete, translate]);

  // Kiểm tra quyền truy cập trang
  if (!canView) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
        <Text text={translate("Bạn không có quyền truy cập trang này")} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
      }}
    >
      <DataGrid
        keyExpr="id"
        height={window.innerHeight - 160}
        dataSource={filteredData}
        isLoading={status === ePageBaseStatus.is_loading}
        title="Danh sách trạng thái đăng ký ăn"
        searchEnable
        exportConfig={{
          enable: true,
          fileName: "danh-sach-trang-thai-dang-ky-an",
        }}
        paging={{
          enable: true,
          pageSize: 20,
          pageSizeItems: [20, 50, 200, 500, 1000],
        }}
        actionComponent={
          <Box
            sx={{
              display: "flex",
            }}
          >
            {canView && (
              <Button
                text="Base.Label.Refresh"
                leadingVisual={SyncIcon}
                size="medium"
                sx={{ mx: 1 }}
                onClick={handleReload}
              />
            )}
            {canAdd && (
              <Button
                text="Base.Label.AddNew"
                leadingVisual={PlusIcon}
                size="medium"
                variant="primary"
                onClick={() => {
                  dispatch(
                    actions.danhMucWrapper.trangThaiDangKyAn.SHOW_EDIT_MODAL(undefined)
                  );
                }}
              />
            )}
          </Box>
        }
        columns={columns}
      />
      {isShowEditModal && <TrangThaiDangKyAnEditModal />}
    </Box>
  );
};

export default TrangThaiDangKyAnPage;
